import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Input, Modal, Select, Space, Spin, Table, Tag, Typography } from 'antd';
import { FolderOpenOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { REFERENCE_TYPES, acqSearchReference, acqGetReferenceById, type ReferenceItem } from './referenceTypes';

interface ReferencePickerProps {
  /** Тип довідника з referenceTypes.ts (наприклад 'counterparty'). */
  acqReferenceType: string;
  /** Поточне значення (id). */
  acqValue?: string | null;
  /** Викликається при виборі. acqLabel — для відображення. */
  acqOnChange?: (acqId: string | null, acqLabel?: string) => void;
  /** Read-only режим: показуємо тільки лейбл (як Tag), без Select. */
  acqReadOnly?: boolean;
  /** Дозволити очистити (показати X). Дефолт: true. */
  acqAllowClear?: boolean;
  acqPlaceholder?: string;
  acqDisabled?: boolean;
  acqSize?: 'small' | 'middle' | 'large';
  /** Фоллбек-лейбл для випадку коли id не резолвиться (BAS UUID без пари в нашій БД). */
  acqFallbackLabel?: string;
}

/**
 * Async-Select з пошуком по серверному довіднику.
 *  • Дебаунс пошуку 250 ms.
 *  • Резолв збереженого value через GET /{type}/{id} при першому рендері.
 *  • Read-only-режим показує label у вигляді Tag (для view-mode форми).
 *  • Якщо id не резолвиться — показує fallback (для legacy BAS UUID, які
 *    ще не змаплені на наш Id).
 *
 * Використання у формі:
 *   <ReferencePicker acqReferenceType="counterparty"
 *                    acqValue={data.counterparty_id}
 *                    acqOnChange={(id) => updateField('counterparty_id', id)} />
 */
export default function ReferencePicker({
  acqReferenceType,
  acqValue,
  acqOnChange,
  acqReadOnly = false,
  acqAllowClear = true,
  acqPlaceholder = 'Оберіть…',
  acqDisabled,
  acqSize = 'middle',
  acqFallbackLabel,
}: ReferencePickerProps) {
  const [acqQuery, setAcqQuery] = useState('');
  const [acqDebounced, setAcqDebounced] = useState('');
  const acqDebounceRef = useRef<NodeJS.Timeout | null>(null);

  // Дебаунс пошуку
  useEffect(() => {
    if (acqDebounceRef.current) clearTimeout(acqDebounceRef.current);
    acqDebounceRef.current = setTimeout(() => setAcqDebounced(acqQuery), 250);
    return () => { if (acqDebounceRef.current) clearTimeout(acqDebounceRef.current); };
  }, [acqQuery]);

  // Поточний обраний запис (для відображення лейбла, поки не виконано пошук)
  const acqCurrentQ = useQuery({
    queryKey: ['ref', acqReferenceType, 'one', acqValue],
    queryFn: () => (acqValue ? acqGetReferenceById(acqReferenceType, acqValue) : Promise.resolve(null)),
    enabled: !!acqValue,
    staleTime: 5 * 60_000,
  });

  // Список результатів пошуку
  const acqSearchQ = useQuery({
    queryKey: ['ref', acqReferenceType, 'search', acqDebounced],
    queryFn: () => acqSearchReference(acqReferenceType, acqDebounced, 30),
    enabled: !acqReadOnly, // у view-mode не шукаємо
    staleTime: 30_000,
  });

  // Опції для Select: пошук + поточне (щоб value завжди мало label)
  const acqOptions = useMemo(() => {
    const acqList: ReferenceItem[] = acqSearchQ.data ?? [];
    const acqMap = new Map(acqList.map((i) => [i.id, i]));
    if (acqCurrentQ.data && !acqMap.has(acqCurrentQ.data.id)) {
      acqMap.set(acqCurrentQ.data.id, acqCurrentQ.data);
    }
    return Array.from(acqMap.values()).map((i) => ({ value: i.id, label: i.label }));
  }, [acqSearchQ.data, acqCurrentQ.data]);

  // useState — БЕЗУМОВНО (Rules of Hooks). Якщо нижче early return — порушує порядок hook'ів.
  const [acqListOpen, setAcqListOpen] = useState(false);

  // ── Read-only вид (для view-mode форми): просто лейбл як Tag
  if (acqReadOnly) {
    if (!acqValue) return <Typography.Text type="secondary">—</Typography.Text>;
    if (acqCurrentQ.isLoading) return <Spin size="small" />;
    if (acqCurrentQ.data) {
      return (
        <Tag style={{ fontSize: 13, padding: '2px 8px' }} title={`id: ${acqValue}`}>
          {acqCurrentQ.data.label}
        </Tag>
      );
    }
    // Не змогли резолвити (наприклад BAS UUID без пари) — показуємо fallback
    return (
      <Tag color="default" title={`id: ${acqValue}`}>
        {acqFallbackLabel ?? `[BAS] ${String(acqValue).slice(0, 8)}…`}
      </Tag>
    );
  }

  // ── Edit-mode (Select)
  const acqHandleChange = (acqNewId: string | undefined) => {
    if (!acqOnChange) return;
    if (!acqNewId) { acqOnChange(null); return; }
    const acqLabel = acqOptions.find((o) => o.value === acqNewId)?.label;
    acqOnChange(acqNewId, acqLabel);
  };

  const acqHandleListPick = (acqId: string, acqLabel?: string) => {
    setAcqListOpen(false);
    if (acqOnChange) acqOnChange(acqId, acqLabel);
  };

  return (
    <>
      <Space.Compact style={{ width: '100%' }}>
        <Select
          showSearch
          allowClear={acqAllowClear}
          filterOption={false}
          placeholder={acqPlaceholder}
          size={acqSize}
          disabled={acqDisabled}
          value={acqValue ?? undefined}
          onChange={acqHandleChange}
          onSearch={setAcqQuery}
          onBlur={() => setAcqQuery('')}
          notFoundContent={acqSearchQ.isLoading ? <Spin size="small" /> : (acqDebounced ? 'Нічого не знайдено' : 'Почніть друкувати')}
          options={acqOptions}
          style={{ width: '100%' }}
        />
        <Button
          size={acqSize}
          icon={<FolderOpenOutlined />}
          disabled={acqDisabled}
          onClick={() => setAcqListOpen(true)}
          title="Відкрити довідник"
        />
      </Space.Compact>
      <ReferenceListPickerModal
        acqOpen={acqListOpen}
        acqReferenceType={acqReferenceType}
        acqOnCancel={() => setAcqListOpen(false)}
        acqOnSelect={acqHandleListPick}
      />
    </>
  );
}

/**
 * Модалка-список довідника (1С "форма вибору"). Open із кнопки "..." біля Select.
 * Підтримує пошук + пагінацію; click на рядок — обирає елемент і закриває модалку.
 */
function ReferenceListPickerModal({
  acqOpen, acqReferenceType, acqOnCancel, acqOnSelect,
}: {
  acqOpen: boolean;
  acqReferenceType: string;
  acqOnCancel: () => void;
  acqOnSelect: (acqId: string, acqLabel?: string) => void;
}) {
  const [acqQuery, setAcqQuery] = useState('');
  const [acqDebounced, setAcqDebounced] = useState('');
  const acqDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const [acqPageSize, setAcqPageSize] = useState(20);

  useEffect(() => {
    if (!acqOpen) { setAcqQuery(''); setAcqDebounced(''); }
  }, [acqOpen]);

  useEffect(() => {
    if (acqDebounceRef.current) clearTimeout(acqDebounceRef.current);
    acqDebounceRef.current = setTimeout(() => setAcqDebounced(acqQuery), 250);
    return () => { if (acqDebounceRef.current) clearTimeout(acqDebounceRef.current); };
  }, [acqQuery]);

  const acqListQ = useQuery({
    queryKey: ['ref', acqReferenceType, 'list', acqDebounced, acqPageSize],
    queryFn: () => acqSearchReference(acqReferenceType, acqDebounced, acqPageSize),
    enabled: acqOpen,
    staleTime: 30_000,
  });

  const acqTitle = REFERENCE_TYPES[acqReferenceType]?.title ?? acqReferenceType;
  const acqRows = acqListQ.data ?? [];

  const acqColumns = [
    {
      title: 'Код',
      dataIndex: ['raw', 'code'],
      key: 'code',
      width: 140,
      render: (_: unknown, acqRow: ReferenceItem) => {
        const acqCode = (acqRow.raw as { code?: string }).code;
        return acqCode ?? <Typography.Text type="secondary">—</Typography.Text>;
      },
    },
    {
      title: 'Назва',
      dataIndex: 'label',
      key: 'label',
    },
  ];

  return (
    <Modal
      open={acqOpen}
      title={`Оберіть: ${acqTitle}`}
      onCancel={acqOnCancel}
      footer={null}
      width={720}
      destroyOnClose
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Input.Search
          placeholder="Пошук…"
          allowClear
          value={acqQuery}
          onChange={(e) => setAcqQuery(e.target.value)}
          autoFocus
        />
        <Table<ReferenceItem>
          rowKey="id"
          size="small"
          dataSource={acqRows}
          columns={acqColumns}
          loading={acqListQ.isLoading || acqListQ.isFetching}
          pagination={{
            pageSize: acqPageSize,
            onShowSizeChange: (_, size) => setAcqPageSize(size),
            showSizeChanger: true,
            pageSizeOptions: ['20', '50', '100'],
          }}
          onRow={(acqRow) => ({
            onClick: () => acqOnSelect(acqRow.id, acqRow.label),
            style: { cursor: 'pointer' },
          })}
        />
      </Space>
    </Modal>
  );
}
